import * as React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import App from "./components/App";
import { highlightDiffsOnSheet, onChangeHandler } from "./services/excel";
import { loadAccountsFromStorage, saveAccountsToStorage } from "./services/magento/accountsStorage";
import { fetchProducts } from "./services/magento/adobeCommerce";
import { setAccounts } from "./slices/accountsSlice";
import { setActiveSheetId } from "./slices/excelSlice";
import { setProducts } from "./slices/productsSlice";
import store from "./store/store";
import "./styles/tailwind.css";

/* global document, Office, module, require, HTMLElement */

const title = "Excel - Magento Connector V1.0";
const PAGE_SIZE = Number(process.env.SYNC_PAGE_SIZE) || 100;

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

function getPageNumberFromName(name: string) {
  const match = name.match(/Page\s+(\d+)/i);
  const page = match ? Number(match[1]) : 1;
  return Number.isNaN(page) || page <= 0 ? 1 : page;
}

/* Render application after Office initializes */
Office.onReady(async () => {
  const persisted = loadAccountsFromStorage();
  store.dispatch(setAccounts(persisted));

  let lastPersisted = JSON.stringify(persisted);
  store.subscribe(() => {
    const state = store.getState();
    const next = {
      accounts: state.accounts.accounts,
      activeAccountId: state.accounts.activeAccountId,
    };
    const serialized = JSON.stringify(next);
    if (serialized !== lastPersisted) {
      saveAccountsToStorage(next.accounts, next.activeAccountId);
      lastPersisted = serialized;
    }
  });

  root?.render(
    <Provider store={store}>
      <App title={title} />
    </Provider>
  );

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load(["id"]);
    await context.sync();
    store.dispatch(setActiveSheetId(sheet.id));
  });

  try {
    const state = store.getState();
    const activeAccount = state.accounts.accounts.find(
      (account) => account.id === state.accounts.activeAccountId
    );
    const activeSheetId = state.excel.activeSheetId;
    if (activeAccount && activeSheetId) {
      const sheetName = await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(activeSheetId);
        sheet.load(["name"]);
        await context.sync();
        return sheet.isNullObject ? "Page 1" : sheet.name;
      });
      const page = getPageNumberFromName(sheetName);
      const { items: products } = await fetchProducts(activeAccount, PAGE_SIZE, page);
      store.dispatch(setProducts(products));
      await highlightDiffsOnSheet(activeSheetId, products);
    }
  } catch (error) {
    console.error("Failed to load products on startup:", error);
  }

  await Excel.run(async (context) => {
    const workbook = context.workbook;
    workbook.worksheets.onChanged.add(onChangeHandler);
    workbook.worksheets.onActivated.add(async (args) => {
      const sheetId = (args as any).worksheetId as string;
      store.dispatch(setActiveSheetId(sheetId));
      const state = store.getState();
      const activeAccount = state.accounts.accounts.find(
        (account) => account.id === state.accounts.activeAccountId
      );
      if (!activeAccount) return;
      const sheetName = await Excel.run(async (ctx) => {
        const sheet = ctx.workbook.worksheets.getItemOrNullObject(sheetId);
        sheet.load(["name"]);
        await ctx.sync();
        return sheet.isNullObject ? "Page 1" : sheet.name;
      });
      const page = getPageNumberFromName(sheetName);
      const { items: products } = await fetchProducts(activeAccount, PAGE_SIZE, page);
      store.dispatch(setProducts(products));
      await highlightDiffsOnSheet(sheetId, products);
    });
    await context.sync();
  });

  // try {
  //   const products = await fetchAllProducts();
  //   store.dispatch(setProducts(products));
  //   await highlightDiffsOnLoad(products);
  // } catch (error) {
  //   console.error("Failed to load Magento products on startup:", error);
  // }
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(
      <Provider store={store}>
        <NextApp title={title} />
      </Provider>
    );
  });
}
